// Rotas e casca da aplicação.
//
// A home depende do perfil (HOME_BY_ROLE, no shared): é para onde o login leva
// e para onde o botão "Ir para a minha home" do error boundary aponta.
// Quem barra acesso de verdade é o guard do backend; <RequireRole> só evita
// mostrar uma tela que o usuário não poderia usar.

import { lazy, Suspense } from 'react'
import { Spin } from 'antd'
import { Navigate, Route, Routes } from 'react-router-dom'
import { HOME_BY_ROLE, type UserRole } from '@c1hd/shared'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AppLayout } from './components/layout/AppLayout'
import { useAuth } from './lib/auth'
import { LoginPage } from './pages/Login'
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordRecovery'
// Carregadas sob demanda: quem é cliente nunca baixa o painel administrativo.
// Se um chunk sumir por causa de um deploy novo, o detector troca a tela pelo
// aviso de nova versão em vez de deixar o error boundary subir.
const SystemsPage = lazy(() =>
  import('./pages/admin/Systems').then((m) => ({ default: m.SystemsPage })),
)
const CategoriesPage = lazy(() =>
  import('./pages/admin/Categories').then((m) => ({ default: m.CategoriesPage })),
)
const SubjectsPage = lazy(() =>
  import('./pages/admin/Subjects').then((m) => ({ default: m.SubjectsPage })),
)
const CompaniesPage = lazy(() =>
  import('./pages/admin/Companies').then((m) => ({ default: m.CompaniesPage })),
)
const ProjectsPage = lazy(() =>
  import('./pages/admin/Projects').then((m) => ({ default: m.ProjectsPage })),
)
const UsersPage = lazy(() =>
  import('./pages/admin/Users').then((m) => ({ default: m.UsersPage })),
)
const AreasPage = lazy(() =>
  import('./pages/admin/Areas').then((m) => ({ default: m.AreasPage })),
)
const CalendarsPage = lazy(() =>
  import('./pages/admin/Calendars').then((m) => ({ default: m.CalendarsPage })),
)
const SlaPoliciesPage = lazy(() =>
  import('./pages/admin/SlaPolicies').then((m) => ({ default: m.SlaPoliciesPage })),
)
const RoutingRulesPage = lazy(() =>
  import('./pages/admin/RoutingRules').then((m) => ({ default: m.RoutingRulesPage })),
)
const NewTicketPage = lazy(() =>
  import('./pages/client/NewTicket').then((m) => ({ default: m.NewTicketPage })),
)
const MyTicketsPage = lazy(() =>
  import('./pages/client/MyTickets').then((m) => ({ default: m.MyTicketsPage })),
)
const ClientTicketDetailPage = lazy(() =>
  import('./pages/client/TicketDetail').then((m) => ({
    default: m.ClientTicketDetailPage,
  })),
)
const SurveyPage = lazy(() =>
  import('./pages/Survey').then((m) => ({ default: m.SurveyPage })),
)
const TicketQueuePage = lazy(() =>
  import('./pages/panel/TicketQueue').then((m) => ({ default: m.TicketQueuePage })),
)
const TicketWorkPage = lazy(() =>
  import('./pages/panel/TicketWork').then((m) => ({ default: m.TicketWorkPage })),
)
const OverviewDashboardPage = lazy(() =>
  import('./pages/dashboards/Overview').then((m) => ({
    default: m.OverviewDashboardPage,
  })),
)
const SlaDashboardPage = lazy(() =>
  import('./pages/dashboards/Sla').then((m) => ({ default: m.SlaDashboardPage })),
)
const SatisfactionDashboardPage = lazy(() =>
  import('./pages/dashboards/Satisfaction').then((m) => ({
    default: m.SatisfactionDashboardPage,
  })),
)
const TeamDashboardPage = lazy(() =>
  import('./pages/dashboards/Team').then((m) => ({ default: m.TeamDashboardPage })),
)
const AuditPage = lazy(() =>
  import('./pages/admin/Audit').then((m) => ({ default: m.AuditPage })),
)
const EscalationRulesPage = lazy(() =>
  import('./pages/admin/EscalationRules').then((m) => ({
    default: m.EscalationRulesPage,
  })),
)

function FullPageSpin() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <Spin size="large" />
    </div>
  )
}

function RequireRole({
  roles,
  children,
}: {
  roles: UserRole[]
  children: React.ReactNode
}) {
  const { user, loading } = useAuth()
  if (loading) return <FullPageSpin />
  if (!user) return <Navigate to="/login" replace />
  if (!roles.includes(user.role)) return <Navigate to={HOME_BY_ROLE[user.role]} replace />
  return <Suspense fallback={<FullPageSpin />}>{children}</Suspense>
}

const STAFF: UserRole[] = ['tecnico', 'administrador']
const ADMIN: UserRole[] = ['administrador']

export function App() {
  const { user, loading } = useAuth()

  if (loading) return <FullPageSpin />

  return (
    <ErrorBoundary role={user?.role}>
      <Routes>
        <Route
          path="/login"
          element={
            user ? <Navigate to={HOME_BY_ROLE[user.role]} replace /> : <LoginPage />
          }
        />
        <Route path="/esqueci-minha-senha" element={<ForgotPasswordPage />} />
        <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
        {/* Abre pelo link do e-mail, com token e sem login. */}
        <Route
          path="/avaliacao"
          element={
            <Suspense fallback={<FullPageSpin />}>
              <SurveyPage />
            </Suspense>
          }
        />

        <Route element={<AppLayout />}>
          {/* Portal do cliente */}
          <Route
            path="/chamados"
            element={
              <RequireRole roles={['cliente', ...STAFF]}>
                <MyTicketsPage />
              </RequireRole>
            }
          />
          <Route
            path="/chamados/novo"
            element={
              <RequireRole roles={['cliente', ...STAFF]}>
                <NewTicketPage />
              </RequireRole>
            }
          />
          <Route
            path="/chamados/:protocol"
            element={
              <RequireRole roles={['cliente', ...STAFF]}>
                <ClientTicketDetailPage />
              </RequireRole>
            }
          />

          {/* Painel do time */}
          <Route
            path="/painel/fila"
            element={
              <RequireRole roles={STAFF}>
                <TicketQueuePage
                  scope="fila"
                  title="Fila de chamados"
                  description="Chamados esperando quem os assuma. Quem assume passa a ser o responsável."
                />
              </RequireRole>
            }
          />
          <Route
            path="/painel/meus"
            element={
              <RequireRole roles={STAFF}>
                <TicketQueuePage
                  scope="meus"
                  title="Meus atendimentos"
                  description="O que está com você agora."
                />
              </RequireRole>
            }
          />
          <Route
            path="/painel/aguardando"
            element={
              <RequireRole roles={STAFF}>
                <TicketQueuePage
                  scope="aguardando"
                  title="Aguardando cliente"
                  description="Chamados parados esperando resposta de quem abriu. O prazo fica pausado."
                />
              </RequireRole>
            }
          />
          <Route
            path="/painel/encerrados"
            element={
              <RequireRole roles={STAFF}>
                <TicketQueuePage
                  scope="encerrados"
                  title="Chamados encerrados"
                  description="Histórico do que já foi concluído ou cancelado."
                />
              </RequireRole>
            }
          />
          <Route
            path="/painel/chamados/:protocol"
            element={
              <RequireRole roles={STAFF}>
                <TicketWorkPage />
              </RequireRole>
            }
          />

          {/* Indicadores */}
          <Route
            path="/gestao/dashboard"
            element={
              <RequireRole roles={STAFF}>
                <OverviewDashboardPage />
              </RequireRole>
            }
          />
          <Route
            path="/gestao/sla"
            element={
              <RequireRole roles={STAFF}>
                <SlaDashboardPage />
              </RequireRole>
            }
          />
          <Route
            path="/gestao/satisfacao"
            element={
              <RequireRole roles={STAFF}>
                <SatisfactionDashboardPage />
              </RequireRole>
            }
          />
          <Route
            path="/gestao/equipe"
            element={
              <RequireRole roles={STAFF}>
                <TeamDashboardPage />
              </RequireRole>
            }
          />

          {/* Administração */}
          <Route
            path="/admin/sistemas"
            element={
              <RequireRole roles={ADMIN}>
                <SystemsPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/categorias"
            element={
              <RequireRole roles={ADMIN}>
                <CategoriesPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/assuntos"
            element={
              <RequireRole roles={ADMIN}>
                <SubjectsPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/usuarios"
            element={
              <RequireRole roles={ADMIN}>
                <UsersPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/empresas"
            element={
              <RequireRole roles={ADMIN}>
                <CompaniesPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/projetos"
            element={
              <RequireRole roles={ADMIN}>
                <ProjectsPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/areas"
            element={
              <RequireRole roles={ADMIN}>
                <AreasPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/calendarios"
            element={
              <RequireRole roles={ADMIN}>
                <CalendarsPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/sla"
            element={
              <RequireRole roles={ADMIN}>
                <SlaPoliciesPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/roteamento"
            element={
              <RequireRole roles={ADMIN}>
                <RoutingRulesPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/escalonamento"
            element={
              <RequireRole roles={ADMIN}>
                <EscalationRulesPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/auditoria"
            element={
              <RequireRole roles={ADMIN}>
                <AuditPage />
              </RequireRole>
            }
          />
        </Route>

        <Route
          path="*"
          element={<Navigate to={user ? HOME_BY_ROLE[user.role] : '/login'} replace />}
        />
      </Routes>
    </ErrorBoundary>
  )
}
